import { IAction, IActionParameter } from "@kuika/kuika-cl-model";
import { PureComponent, ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import { isOptionalParameterMenuOffered } from "./action-parameter-surface";
import {
  type IOptionalParameterMenuContext,
  isOptionalActionParameterOffered,
  isOptionalActionParameterSelectableInMenu
} from "./action-parameter-visibility";

export interface IOptionalParameterListState {
  optionalParameterMenuVisibility: boolean;
  pendingParameters: IActionParameter[];
}

export interface IOptionalParameterListProps {
  action: IAction;
  onOptionalParameterSave: (optionalParameters: IActionParameter[]) => void;
}

class OptionalParameterList extends PureComponent<
  IOptionalParameterListProps & IWithSDProps & IWithContextProp,
  IOptionalParameterListState
> {
  constructor(props: IOptionalParameterListProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      optionalParameterMenuVisibility: false,
      pendingParameters: []
    };
  }

  componentDidUpdate(
    prevProps: IOptionalParameterListProps & IWithSDProps & IWithContextProp,
    prevState: IOptionalParameterListState
  ) {
    if (!prevState.optionalParameterMenuVisibility && this.state.optionalParameterMenuVisibility) {
      this.setState({
        pendingParameters: this.getOptionalParameters()
      });
    }
  }

  onOptionalParameterSave = () => {
    this.setState({ optionalParameterMenuVisibility: false }, () => {
      this.props.onOptionalParameterSave(this.state.pendingParameters);
    });
  };

  getOptionalParameters = (): IActionParameter[] => {
    const actionDefinition = this.props.sd.actionDefinitions.find((p) => p.id === this.props.action.actionDefinitionId);
    if (!actionDefinition) {
      return [];
    }

    const optionalParameters: IActionParameter[] = [];

    actionDefinition.actionParameters.forEach((actionDefParam: IActionParameter) => {
      if (isOptionalActionParameterOffered(actionDefParam)) {
        const existingParam = this.props.action.parameters.find((p) => p.name === actionDefParam.name);

        if (existingParam) {
          optionalParameters.push(existingParam);
        } else {
          optionalParameters.push(actionDefParam);
        }
      }
    });

    return optionalParameters;
  };

  // Parameters that should not be offered in the picker for the current context.
  isParameterSelectable = (parameter: IActionParameter): boolean => {
    const context: IOptionalParameterMenuContext = { projectType: this.props.sd.getProject().projectType };
    if (!isOptionalActionParameterSelectableInMenu(parameter, context)) return false;

    if (this.props.action.label === "Search" && parameter.name === "sessionId") {
      const searchTypeParamValue = this.props.action.parameters.find((p) => p.name === "searchType");
      if (searchTypeParamValue?.symbol.fixedValue !== "2") return false;
    }

    return true;
  };

  // Toggles against the PENDING set, which is every optional parameter — not the
  // `isParameterSelectable` view the menu shows. Rebuilding state from the filtered list would
  // drop the parameters this context hides from whatever is saved next.
  onParameterVisibilityChange = (parameter: IActionParameter): void => {
    this.setState((prevState) => ({
      pendingParameters: prevState.pendingParameters.map((param) =>
        param.name === parameter.name ? { ...param, isExternalVisible: !param.isExternalVisible } : param
      )
    }));
  };

  handleOptionalParameterMenuVisibility = (visible: boolean) => {
    this.setState({ optionalParameterMenuVisibility: visible });

    if (!visible) {
      this.setState({ pendingParameters: [] });
    }
  };

  render(): ReactNode {
    if (!isOptionalParameterMenuOffered(this.props.action.label)) {
      return <></>;
    }

    const allOptionalParameters =
      this.state.pendingParameters.length > 0 ? this.state.pendingParameters : this.getOptionalParameters();

    const optionalParameters = allOptionalParameters.filter(this.isParameterSelectable);

    // Nothing selectable in this context — don't offer an empty picker.
    if (optionalParameters.length === 0) {
      return null;
    }

    // A row and its checkbox both toggle the same parameter: the checkbox's wrapper stops the
    // click from reaching the row, so exactly one of the two handlers runs per click.
    const parameterOptions: KuikaDropdownMenuItemType[] = optionalParameters.map(
      (parameter: IActionParameter): KuikaDropdownMenuItemType => ({
        key: `${this.props.action.id}-optional-parameter-${parameter.name}`,
        label: (
          <KuikaCheckbox
            checked={parameter.isExternalVisible === true}
            onChange={() => this.onParameterVisibilityChange(parameter)}
            label={parameter.displayName ?? parameter.name}
          />
        ),
        // The label is a node, so the row's derived test id would read "[object Object]".
        dataTestId: `optional-parameter-${parameter.name}`,
        onClick: () => this.onParameterVisibilityChange(parameter),
        showSelectedIcon: false
      })
    );

    return (
      <KuikaDropdownMenu
        options={parameterOptions}
        placement="bottomLeft"
        trigger="click"
        open={this.state.optionalParameterMenuVisibility}
        setOpen={this.handleOptionalParameterMenuVisibility}
        // Ticking a parameter must not dismiss the menu — the list is chosen, then saved.
        closeOnSelect={false}
        footer={
          <div className="kuika-flex-end" style={{ width: "100%" }}>
            <KuikaButton type="text" size="sm" onClick={this.onOptionalParameterSave}>
              SAVE
            </KuikaButton>
          </div>
        }
      >
        <span className={"kuika_addNavigationLink"} style={{ margin: 0, background: "transparent" }}>
          + ADD OPTIONAL PARAMETER
        </span>
      </KuikaDropdownMenu>
    );
  }
}

const optionalParameterList = withContext<IOptionalParameterListProps>(
  withSD<IOptionalParameterListProps>(OptionalParameterList)
);
export { optionalParameterList as OptionalParameterList };
